import { useMemo } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Menu } from "@organisms"
import type { MenuItemType } from "@organisms/Menu/Menu.types"
import NavBottomBarItem from "@organisms/NavBottomBar/NavBottomBarItem/NavBottomBarItem"

import { CLIENTS_ROUTES } from "@routes/clients"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import { NavBottomBarCreateObjectProps } from "./NavBottomBarCreateObject.types"

export default function NavBottomBarCreateObject(props: NavBottomBarCreateObjectProps) {
    const { isDisabled = false, _isFocused = false, _isHovered = false, isPressed = false } = props

    const { user } = useUser()
    const { hasEntitlement, hasPermission } = useHasAccess()

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [
        estimateCreateRoute,
        jobsCreateRoute,
        invoicesCreateRoute,
        purchaseOrdersCreateRoute,
        vendorsCreateRoute,
        clientsCreateRoute,
        pricebookServiceCreateRoute,
        pricebookPartOrMaterialCreateRoute,
        pricebookMiscellaneousCreateRoute,
        pricebookDiscountCreateRoute,
        pricebookTaxRatesCreateRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.CREATE,
        JOBS_ROUTES.CREATE,
        INVOICES_ROUTES.CREATE,
        PURCHASE_ORDER_ROUTES.CREATE,
        VENDOR_ROUTES.CREATE,
        CLIENTS_ROUTES.CREATE,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
    ])

    const menuContentItems = useMemo(() => {
        const items: MenuItemType[] = []

        if (
            hasEntitlement("entitlement_estimates_enabled") &&
            hasPermission("estimates_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.estimate.icon,
                path: estimateCreateRoute,
                title: objectMap.estimate.label,
            })
        }

        if (
            hasEntitlement("entitlement_jobs_enabled") &&
            hasPermission("jobs_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.job.icon,
                path: jobsCreateRoute,
                title: objectMap.job.label,
            })
        }

        if (
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.invoice.icon,
                path: invoicesCreateRoute,
                title: objectMap.invoice.label,
            })
        }

        if (
            hasEntitlement("entitlement_purchase_orders_enabled") &&
            hasPermission("purchase_orders_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.purchase_order.icon,
                path: purchaseOrdersCreateRoute,
                title: objectMap.purchase_order.label,
            })
        }

        if (hasPermission("vendors_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.vendor.icon,
                path: vendorsCreateRoute,
                title: objectMap.vendor.label,
            })
        }

        if (hasPermission("clients_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.client.icon,
                path: clientsCreateRoute,
                title: objectMap.client.label,
            })
        }

        if (hasPermission("pricebook_create_permission", PERMISSION_LEVEL.FULL)) {
            const pricebookItems: MenuItemType[] = [
                { title: "Service", path: pricebookServiceCreateRoute },
                { title: "Part or Material", path: pricebookPartOrMaterialCreateRoute },
                { title: "Other", path: pricebookMiscellaneousCreateRoute },
                { title: "Discount", path: pricebookDiscountCreateRoute },
            ]

            if (isUsingTaxRates) {
                pricebookItems.push({ title: "Tax Rate", path: pricebookTaxRatesCreateRoute })
            }

            items.push({
                icon: objectMap.pricebook_item.icon,
                title: objectMap.pricebook_item.label,
                submenu: {
                    header: { title: "Create Pricebook Item" },
                    items: [{ type: "group", items: pricebookItems }],
                },
            })
        }

        return items
    }, [
        hasEntitlement,
        hasPermission,
        isUsingTaxRates,
        estimateCreateRoute,
        jobsCreateRoute,
        invoicesCreateRoute,
        purchaseOrdersCreateRoute,
        vendorsCreateRoute,
        clientsCreateRoute,
        pricebookServiceCreateRoute,
        pricebookPartOrMaterialCreateRoute,
        pricebookMiscellaneousCreateRoute,
        pricebookDiscountCreateRoute,
        pricebookTaxRatesCreateRoute,
    ])

    return (
        <Menu
            title="Create Object"
            trigger={
                <NavBottomBarItem
                    title="Create Object"
                    icon={faIcon({ name: "plus", style: "regular" })}
                    iconActive={faIcon({ name: "plus", style: "solid" })}
                    isDisabled={isDisabled}
                    _isFocused={_isFocused}
                    _isHovered={_isHovered}
                    isPressed={isPressed}
                />
            }
            items={[{ type: "group", items: menuContentItems }]}
        />
    )
}
